import { MetaMaskAdapter } from "@metamask/connect-tron"
import { isMetamaskInstalled } from "@metamask/multichain-api-client"
import { TronWeb } from "tronweb"

const TRON_FULL_HOST = "https://api.trongrid.io"
const TRON_PROVIDER_KEY = "web3:tron-provider"

export type TronConnectBackend = "injected" | "metamask"

let adapter: MetaMaskAdapter | null = null

function getAdapter(): MetaMaskAdapter {
  adapter ??= new MetaMaskAdapter()
  return adapter
}

export function getReadonlyTronWeb(): TronWeb {
  return new TronWeb({ fullHost: TRON_FULL_HOST })
}

export function getActiveTronBackend(): TronConnectBackend {
  if (typeof sessionStorage === "undefined") return "injected"
  return sessionStorage.getItem(TRON_PROVIDER_KEY) === "metamask"
    ? "metamask"
    : "injected"
}

export function setActiveTronBackend(backend: TronConnectBackend): void {
  sessionStorage.setItem(TRON_PROVIDER_KEY, backend)
}

export async function isMetaMaskTronAvailable(): Promise<boolean> {
  return isMetamaskInstalled({ timeout: 500 })
}

export function isMetaMaskTronSelected(): boolean {
  return getActiveTronBackend() === "metamask"
}

export function readMetaMaskTronAddress(): string | null {
  if (!isMetaMaskTronSelected()) return null
  const address = getAdapter().address
  return typeof address === "string" && address.length > 0 ? address : null
}

export async function connectMetaMaskTron(): Promise<string> {
  setActiveTronBackend("metamask")
  const mm = getAdapter()
  await mm.connect()
  const address = mm.address
  if (!address) {
    throw new Error("MetaMask TRON connection failed")
  }
  return address
}

export async function disconnectMetaMaskTron(): Promise<void> {
  try {
    await getAdapter().disconnect()
  } finally {
    if (getActiveTronBackend() === "metamask") {
      sessionStorage.removeItem(TRON_PROVIDER_KEY)
    }
  }
}

export async function approveTronUsdtViaMetaMask(
  tokenAddress: string,
  spenderBase58: string,
  ownerBase58: string,
): Promise<string> {
  const tronWeb = getReadonlyTronWeb()
  const mm = getAdapter()
  if (!mm.address) {
    throw new Error("MetaMask TRON not connected")
  }

  const spenderHex = tronWeb.address.toHex(spenderBase58)
  const max =
    "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff"

  const built = await tronWeb.transactionBuilder.triggerSmartContract(
    tokenAddress,
    "approve(address,uint256)",
    { feeLimit: 100_000_000, callValue: 0 },
    [
      { type: "address", value: spenderHex },
      { type: "uint256", value: max },
    ],
    ownerBase58,
  )

  const transaction = built.transaction
  if (!transaction) {
    throw new Error("Failed to build TRON approve transaction")
  }

  const signed = await mm.signTransaction(transaction)
  const result = await tronWeb.trx.sendRawTransaction(signed)
  if (!result.result) {
    throw new Error(
      typeof result.message === "string"
        ? result.message
        : "TRON transaction broadcast failed",
    )
  }
  return result.txid ?? signed.txID
}
